import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Store,
  Package,
  ShoppingCart,
  TrendingUp,
  DollarSign,
  Star,
  Eye,
  Plus,
  ArrowRight,
  ArrowUpRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Truck,
  BarChart3,
  Settings,
  CreditCard,
  Gift
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [seller, setSeller] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSellerData();
  }, []);

  const fetchSellerData = async () => {
    try {
      // Fetch seller profile
      const sellerRes = await axios.get(`${API}/api/sellers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeller(sellerRes.data);

      // Fetch orders
      const ordersRes = await axios.get(`${API}/api/sellers/me/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(ordersRes.data);

      // Fetch products
      const productsRes = await axios.get(`${API}/api/sellers/me/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(productsRes.data);

      // Fetch analytics
      const analyticsRes = await axios.get(`${API}/api/sellers/me/analytics?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(analyticsRes.data);

    } catch (err) {
      if (err.response?.status === 404) {
        navigate("/seller/register");
      } else {
        toast.error("Kunne ikke laste data");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!seller) return null;

  const pendingOrders = orders.filter(o => o.status === "pending" || o.status === "confirmed");
  const freeTierProgress = seller.free_tier ? 
    Math.min(100, (seller.total_revenue / seller.free_tier.threshold) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {seller.logo_url ? (
              <img 
                src={seller.logo_url} 
                alt={seller.company_name}
                className="w-16 h-16 rounded-xl object-cover border border-white/10"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#F7931A] to-[#FFCD00] flex items-center justify-center">
                <Store className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-['Unbounded'] font-bold text-white">
                  {seller.company_name}
                </h1>
                {seller.verified && (
                  <Badge className="bg-blue-500/20 text-blue-400">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verifisert
                  </Badge>
                )}
              </div>
              <p className="text-[#94A3B8] font-mono">{seller.seller_code}</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Link to="/seller/products">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <Package className="w-4 h-4 mr-2" />
                Mine Produkter
              </Button>
            </Link>
            <Link to="/seller/settings">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <Settings className="w-4 h-4 mr-2" />
                Innstillinger
              </Button>
            </Link>
            <Link to="/seller/products">
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                <Plus className="w-4 h-4 mr-2" />
                Legg Til Produkt
              </Button>
            </Link>
          </div>
        </div>

        {/* Free Tier Banner */}
        {seller.free_tier?.in_free_tier && (
          <Card className="bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Gift className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Gratis Tier Aktiv!</h3>
                    <p className="text-[#94A3B8] text-sm">
                      {seller.free_tier.remaining_free.toLocaleString()} NOK igjen uten provisjon
                    </p>
                  </div>
                </div>
                <div className="flex-1 max-w-md">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#94A3B8]">Brukt</span>
                    <span className="text-white">
                      {seller.total_revenue.toLocaleString()} / {seller.free_tier.threshold.toLocaleString()} NOK
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all"
                      style={{ width: `${freeTierProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-[#F7931A]/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#F7931A]" />
                </div>
                <Badge variant="outline" className="border-green-500/50 text-green-400 text-xs">
                  +12%
                </Badge>
              </div>
              <p className="text-2xl font-bold text-white">
                {seller.total_revenue?.toLocaleString() || 0} NOK
              </p>
              <p className="text-[#94A3B8] text-sm">Total Omsetning</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-[#23F7DD]/20 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-[#23F7DD]" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{seller.total_sales || 0}</p>
              <p className="text-[#94A3B8] text-sm">Solgte Enheter</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{products.length}</p>
              <p className="text-[#94A3B8] text-sm">Aktive Produkter</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white flex items-center">
                {seller.rating || 0}
                <span className="text-sm text-[#94A3B8] ml-1">/ 5</span>
              </p>
              <p className="text-[#94A3B8] text-sm">{seller.review_count || 0} Anmeldelser</p>
            </CardContent>
          </Card>
        </div>

        {/* Orders & Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#23F7DD]" />
                Siste Ordrer
              </CardTitle>
              <Link to="/seller/orders">
                <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-white">
                  Se alle
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {pendingOrders.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400 text-sm font-medium">
                    {pendingOrders.length} ordre{pendingOrders.length > 1 ? 'r' : ''} venter på behandling
                  </span>
                </div>
              )}
              
              {orders.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Ingen ordrer ennå</p>
                  <p className="text-sm">Ordrer vil vises her når kunder kjøper dine produkter</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 5).map(order => (
                    <div 
                      key={order.order_id} 
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          order.status === 'pending' ? 'bg-yellow-400' :
                          order.status === 'confirmed' ? 'bg-blue-400' :
                          order.status === 'shipped' ? 'bg-purple-400' :
                          order.status === 'delivered' ? 'bg-green-400' :
                          'bg-red-400'
                        }`} />
                        <div>
                          <p className="text-white font-medium">#{order.order_id.slice(-8)}</p>
                          <p className="text-[#94A3B8] text-xs">
                            {order.items.length} produkt{order.items.length > 1 ? 'er' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">
                          {order.seller_subtotal?.toLocaleString() || 0} NOK
                        </p>
                        <p className="text-[#94A3B8] text-xs">
                          {new Date(order.created_at).toLocaleDateString('no-NO')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Products */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#F7931A]" />
                Topp Produkter
              </CardTitle>
              <Link to="/seller/products">
                <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-white">
                  Se alle
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <div className="text-center py-8 text-[#94A3B8]">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Ingen produkter ennå</p>
                  <Link to="/seller/products">
                    <Button className="mt-3 bg-[#23F7DD] text-[#050505]">
                      <Plus className="w-4 h-4 mr-2" />
                      Legg Til Produkt
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {(analytics?.top_products || products.slice(0, 5)).map((product, idx) => (
                    <div 
                      key={product.product_id || product._id || idx} 
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                    >
                      <div className="w-8 h-8 bg-[#F7931A]/20 rounded-lg flex items-center justify-center text-[#F7931A] font-bold">
                        #{idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium truncate">
                          {product.name}
                        </p>
                        <p className="text-[#94A3B8] text-xs">
                          {product.total_sold || 0} solgt
                        </p>
                      </div>
                      <p className="text-[#23F7DD] font-medium">
                        {(product.revenue || product.price || 0).toLocaleString()} NOK
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/webshop">
            <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#23F7DD]/50 transition-colors cursor-pointer group">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#23F7DD]/20 rounded-lg flex items-center justify-center group-hover:bg-[#23F7DD]/30 transition-colors">
                  <Plus className="w-5 h-5 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-white font-medium">Nytt Produkt</p>
                  <p className="text-[#94A3B8] text-xs">Legg til</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/seller/orders">
            <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#F7931A]/50 transition-colors cursor-pointer group">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F7931A]/20 rounded-lg flex items-center justify-center group-hover:bg-[#F7931A]/30 transition-colors">
                  <Truck className="w-5 h-5 text-[#F7931A]" />
                </div>
                <div>
                  <p className="text-white font-medium">Ordrer</p>
                  <p className="text-[#94A3B8] text-xs">Behandle</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/seller/payouts">
            <Card className="bg-[#0A0A0A] border-white/10 hover:border-green-500/50 transition-colors cursor-pointer group">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                  <CreditCard className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Utbetalinger</p>
                  <p className="text-[#94A3B8] text-xs">Se oversikt</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/seller/analytics">
            <Card className="bg-[#0A0A0A] border-white/10 hover:border-purple-500/50 transition-colors cursor-pointer group">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Statistikk</p>
                  <p className="text-[#94A3B8] text-xs">Se rapport</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Public Store Link */}
        <Card className="bg-gradient-to-r from-[#F7931A]/10 to-[#FFCD00]/10 border-[#F7931A]/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-semibold mb-1">Din Butikk</h3>
                <p className="text-[#94A3B8] text-sm">
                  Se hvordan kundene ser butikken din på AllStillas Marketplace
                </p>
              </div>
              <Link to={`/nettbutikk/seller/${seller.seller_code}`}>
                <Button className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80">
                  <Eye className="w-4 h-4 mr-2" />
                  Se Min Butikk
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
